/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { widgetStyles, animations } from '../../utils/designTokens';
import { CircularProgress } from '@mui/material';
import PortalModal from '../../components/shared/PortalModal';

// Brand accent color
const ACCENT = '#7d6633';



// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-3 md:p-4 lg:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
      <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm md:text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 hidden md:block">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Icons using SVG for consistency with client page style
const Icons = {
  Search: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Plus: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Edit: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Toggle: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Active: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  Inactive: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Delete: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
};

function getUrlShopId() {
  try {
    return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
  } catch {
    return null;
  }
}

const Services = () => {
  const { isDark } = useTheme();
  const { user } = useAdminAuth();
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    q: '',
    page: 1,
    pageSize: 25
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteErrorModal, setShowDeleteErrorModal] = useState(false);
  const [deleteErrorData, setDeleteErrorData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [focusServiceId, setFocusServiceId] = useState(null);
  
  // Barbers list for assignment
  const [barbers, setBarbers] = useState([]);
  const [loadingBarbers, setLoadingBarbers] = useState(false);
  
  // Create service form state
  const [createForm, setCreateForm] = useState({
    name: '',
    duration_min: '',
    description: '',
    price_cents: '',
    active: true,
    barber_user_ids: []
  });
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);

  // Edit service form state
  const [editForm, setEditForm] = useState({
    name: '',
    duration_min: '',
    description: '',
    price_cents: '',
    barber_user_ids: []
  });
  const [editErrors, setEditErrors] = useState({});
  const [editing, setEditing] = useState(false);

  // Load services with current filters
  const loadServices = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await axiosClient.get(`/admin/services?${params.toString()}`);
      setServices(response.data.items || []);
      setPagination(response.data.pagination || {});
      
    } catch (error) {
      console.error('Error loading services:', error);
      setError(t('admin.failed_to_load_services'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  // Load barbers for service assignment
  const loadBarbers = useCallback(async () => {
    setLoadingBarbers(true);
    try {
      const response = await axiosClient.get('/admin/services/barbers');
      setBarbers(response.data.barbers || []);
    } catch (error) {
      console.error('Error loading barbers:', error);
    } finally {
      setLoadingBarbers(false);
    }
  }, []);

  // Load services on mount and filter changes
  useEffect(() => {
    loadServices();
    loadBarbers();
  }, [loadServices, loadBarbers]);

  // Accept deep-links from global search: ?q=...&focus_service_id=123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const focusId = params.get('focus_service_id');

    if (q && q !== filters.q) {
      setFilters(prev => ({ ...prev, q, page: 1 }));
    }
    if (focusId) {
      const n = Number(focusId);
      if (Number.isFinite(n)) setFocusServiceId(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // After services render, scroll & highlight the focused service
  useEffect(() => {
    if (!focusServiceId) return;
    if (!services || services.length === 0) return;
    const row = document.getElementById(`service-row-${focusServiceId}`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const t = setTimeout(() => setFocusServiceId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [focusServiceId, services]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleStatus = async (service) => {
    const newStatus = !service.is_active;
    
    setConfirmData({
      type: 'toggle',
      title: newStatus ? t('admin.enable_service_title') : t('admin.disable_service_title'),
      message: newStatus ? t('admin.are_you_sure_enable_service', { name: service.name }) : t('admin.are_you_sure_disable_service', { name: service.name }),
      onConfirm: () => performToggle(service.id, newStatus)
    });
    setShowConfirmModal(true);
  };

  const performToggle = async (serviceId, newStatus) => {
    try {
      const response = await axiosClient.patch(`/admin/services/${serviceId}/status`, {
        active: newStatus
      });
      
      // Reload services
      loadServices();
      
      // Show success message
      setSuccessMessage(newStatus ? t('admin.service_success_enabled') : t('admin.service_success_disabled'));
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error toggling service status:', error);
      setError(t('admin.failed_to_delete_service'));
    }
  };

  const handleDeleteService = async (service) => {
    setConfirmData({
      type: 'delete',
      title: t('admin.delete_service_confirm_title'),
      message: t('admin.delete_service_confirm_message'),
      details: [
        t('admin.service_detail', { name: service.name }),
        t('admin.duration_detail', { duration: formatDuration(service.duration_min) }),
        t('admin.price_detail', { price: formatPrice(service.price_cents) })
      ],
      warning: t('admin.cannot_be_undone_warning'),
      onConfirm: () => performDeleteService(service)
    });
    setShowConfirmModal(true);
  };

  const performDeleteService = async (service) => {

    try {
      const response = await axiosClient.delete(`/admin/services/${service.id}`);
      
      if (response.data.success) {
        // Remove service from the list
        setServices(prev => prev.filter(s => s.id !== service.id));
        
        // Update pagination total
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
        
        setSuccessMessage(t('admin.service_success_deleted'));
        setTimeout(() => setSuccessMessage(''), 3000);
        
        console.log('✅ Service deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      
      // Handle 409 Conflict specifically
      if (error.response?.status === 409) {
        setDeleteErrorData({
          serviceName: service.name,
          bookingsCount: error.response?.data?.bookingsCount || 'some',
          message: error.response?.data?.message || 'Cannot delete service that has existing bookings'
        });
        setShowDeleteErrorModal(true);
      } else if (error.response?.data?.code === 'SERVICE_HAS_BOOKINGS') {
        setDeleteErrorData({
          serviceName: service.name,
          bookingsCount: error.response?.data?.bookingsCount || 'some',
          message: error.response?.data?.message || 'Cannot delete service that has existing bookings'
        });
        setShowDeleteErrorModal(true);
      } else {
        setError(t('admin.failed_to_delete_service'));
      }
    }
  };

  // Handle create service form
  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (createErrors[field]) {
      setCreateErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateErrors({});

    try {
      // Prepare payload
      const payload = {
        name: createForm.name.trim(),
        duration_min: parseInt(createForm.duration_min) || 0,
        active: createForm.active,
        barber_user_ids: createForm.barber_user_ids
      };

      // Add optional fields if provided
      if (createForm.description.trim()) {
        payload.description = createForm.description.trim();
      }
      if (createForm.price_cents) {
        payload.price_cents = parseInt(createForm.price_cents) || 0;
      }

      const response = await axiosClient.post('/admin/services', payload);

      if (response.data) {
        // Prepend new service to the list
        setServices(prevServices => [
          {
            ...response.data,
            is_active: response.data.active // Map for frontend consistency
          },
          ...prevServices
        ]);
        
        // Reset form and close modal
        setCreateForm({
          name: '',
          duration_min: '',
          description: '',
          price_cents: '',
          active: true,
          barber_user_ids: []
        });
        setShowCreateModal(false);
        setSuccessMessage(t('admin.service_success_created'));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Create service error:', error);
      
      if (error.response?.data?.fields) {
        // Handle field-specific validation errors
        setCreateErrors(error.response.data.fields);
      } else {
        setCreateErrors({
          general: error.response?.data?.message || 'Failed to create service'
        });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({
      name: '',
      duration_min: '',
      description: '',
      price_cents: '',
      active: true,
      barber_user_ids: []
    });
    setCreateErrors({});
  };

  // Handle edit service form
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEditService = async (e) => {
    e.preventDefault();
    setEditing(true);
    setEditErrors({});

    try {
      // Prepare payload - only send changed fields
      const payload = {};
      if (editForm.name.trim() !== (selectedService.name || '')) {
        payload.name = editForm.name.trim();
      }
      if (parseInt(editForm.duration_min) !== (selectedService.duration_min || 0)) {
        payload.duration_min = parseInt(editForm.duration_min) || 0;
      }
      if (editForm.description.trim() !== (selectedService.description || '')) {
        payload.description = editForm.description.trim();
      }
      if (parseInt(editForm.price_cents) !== (selectedService.price_cents || 0)) {
        payload.price_cents = parseInt(editForm.price_cents) || 0;
      }
      
      // Check if barber assignments changed
      const currentBarberIds = (selectedService.assigned_barbers || []).map(b => b.id).sort();
      const newBarberIds = (editForm.barber_user_ids || []).sort();
      if (JSON.stringify(currentBarberIds) !== JSON.stringify(newBarberIds)) {
        payload.barber_user_ids = editForm.barber_user_ids;
      }

      // If no changes, just close modal
      if (Object.keys(payload).length === 0) {
        handleCloseEditModal();
        return;
      }

      const response = await axiosClient.patch(`/admin/services/${selectedService.id}`, payload);

      if (response.data) {
        // Update service in the list
        setServices(prevServices =>
          prevServices.map(s =>
            s.id === selectedService.id
              ? {
                  ...response.data,
                  is_active: response.data.active // Map for frontend consistency
                }
              : s
          )
        );
        
        handleCloseEditModal();
        setSuccessMessage(t('admin.service_success_updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Edit service error:', error);
      
      if (error.response?.data?.fields) {
        // Handle field-specific validation errors
        setEditErrors(error.response.data.fields);
      } else {
        setEditErrors({
          general: error.response?.data?.message || 'Failed to update service'
        });
      }
    } finally {
      setEditing(false);
    }
  };

  const handleOpenEditModal = (service) => {
    setSelectedService(service);
    setEditForm({
      name: service.name || '',
      duration_min: service.duration_min ? service.duration_min.toString() : '',
      description: service.description || '',
      price_cents: service.price_cents ? service.price_cents.toString() : '',
      barber_user_ids: service.assigned_barbers ? service.assigned_barbers.map(b => b.id) : []
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedService(null);
    setEditForm({
      name: '',
      duration_min: '',
      description: '',
      price_cents: '',
      barber_user_ids: []
    });
    setEditErrors({});
  };

  const formatPrice = (priceCents) => {
    if (!priceCents) return t('admin.service_not_set', { defaultValue: 'Not set' });
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  };

  const canEdit = user?.role === 'shop_admin';

  // Select all functionality
  const handleSelectAll = () => {
    if (selectedServices.size === services.length) {
      // Deselect all
      setSelectedServices(new Set());
    } else {
      // Select all visible services
      setSelectedServices(new Set(services.map(service => service.id)));
    }
  };

  const handleSelectService = (serviceId) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const isAllSelected = services.length > 0 && selectedServices.size === services.length;
  const isPartiallySelected = selectedServices.size > 0 && selectedServices.size < services.length;

  // Bulk actions
  const handleBulkActivate = async () => {
    if (selectedServices.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const resolvedShopId = user?.shop_id || getUrlShopId() || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const activatePromises = Array.from(selectedServices).map(serviceId => 
        axiosClient.patch(`/admin/services/${serviceId}/activate?shop_id=${resolvedShopId}`)
      );
      
      await Promise.all(activatePromises);
      
      console.log(`✅ Successfully activated ${selectedServices.size} service(s)`);
      setSelectedServices(new Set());
      loadServices();
    } catch (error) {
      console.error('Error activating services:', error);
      setError(`Failed to activate ${selectedServices.size} service(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedServices.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const resolvedShopId = user?.shop_id || getUrlShopId() || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const deactivatePromises = Array.from(selectedServices).map(serviceId => 
        axiosClient.patch(`/admin/services/${serviceId}/deactivate?shop_id=${resolvedShopId}`)
      );
      
      await Promise.all(deactivatePromises);
      
      console.log(`✅ Successfully deactivated ${selectedServices.size} service(s)`);
      setSelectedServices(new Set());
      loadServices();
    } catch (error) {
      console.error('Error deactivating services:', error);
      setError(`Failed to deactivate ${selectedServices.size} service(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedServices.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const resolvedShopId = user?.shop_id || getUrlShopId() || null;
      if (!resolvedShopId) throw new Error('Missing shop id');
      const deletePromises = Array.from(selectedServices).map(serviceId => 
        axiosClient.delete(`/admin/services/${serviceId}?shop_id=${resolvedShopId}`)
      );
      
      await Promise.all(deletePromises);
      
      console.log(`✅ Successfully deleted ${selectedServices.size} service(s)`);
      setSelectedServices(new Set());
      loadServices();
    } catch (error) {
      console.error('Error deleting services:', error);
      setError(`Failed to delete ${selectedServices.size} service(s)`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedServices(new Set());
  }, [filters.q, filters.status, pagination.page]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              {t('admin.services_page_title')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.services_subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {canEdit && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-3 md:px-4 py-2 rounded-xl text-white text-sm md:text-base font-medium flex items-center gap-1.5 md:gap-2 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
                style={{ background: ACCENT }}
              >
                <Icons.Plus />
                <span className="hidden xs:inline">{t('admin.create_service')}</span>
                <span className="xs:hidden">{t('admin.create')}</span>
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 md:mb-6">
        <CardHeader
          title={t('admin.filters')}
          subtitle={t('admin.filter_services_subtitle')}
          icon={
            <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          }
        />
        <div className="p-3 md:p-4 lg:p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.col_status')}
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">{t('admin.all_services')}</option>
                <option value="active">{t('admin.active_only')}</option>
                <option value="disabled">{t('admin.disabled_only')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.search_service')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 md:pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 md:h-5 md:w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={filters.q}
                  onChange={(e) => handleFilterChange('q', e.target.value)}
                  placeholder={t('admin.name_or_description_placeholder')}
                  className="w-full pl-8 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedServices.size > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10 dark:border-white/10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.selected_count', { count: selectedServices.size })}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBulkActivate}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-sm rounded-lg flex items-center text-white disabled:opacity-50 transition-all"
                  style={{ backgroundColor: ACCENT }}
                >
                  {bulkActionLoading ? <Icons.Toggle /> : <Icons.Active />}
                  <span className="ml-1">{t('admin.activate')}</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBulkDeactivate}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-sm rounded-lg flex items-center bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 transition-all"
                >
                  {bulkActionLoading ? <Icons.Toggle /> : <Icons.Inactive />}
                  <span className="ml-1">{t('admin.deactivate')}</span>
                </motion.button>
                {user?.role === 'shop_admin' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-sm rounded-lg flex items-center bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-all"
                  >
                    {bulkActionLoading ? <Icons.Toggle /> : <Icons.Delete />}
                    <span className="ml-1">{t('admin.delete')}</span>
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-xl">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-xl">
          {successMessage}
        </div>
      )}

      {/* Services Table */}
      <Card>
        <CardHeader
          title={t('admin.services_page_title')}
          subtitle={t('admin.total_services', { count: pagination.total })}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          }
        />
        <div className="admin-table-container admin-table-container--mobile-scroll">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell tablet-hide-col">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                <th className="py-4 px-4 font-medium">{t('admin.col_service')}</th>
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_duration')}</th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell tablet-hide-col">{t('admin.col_duration')}</th>
                {/* Mobile-only columns (use horizontal swipe-scroll) */}
                <th className="py-4 px-4 font-medium md:hidden">{t('admin.col_price')}</th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell tablet-hide-col">{t('admin.col_price')}</th>
                <th className="py-4 px-4 font-medium">{t('admin.col_status')}</th>
                {canEdit && (
                  <th className="py-4 px-4 font-medium text-right">{t('admin.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 dark:divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                      <CircularProgress size={24} style={{ color: ACCENT }} />
                      <span>{t('admin.loading_services')}</span>
                    </div>
                  </td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="py-12 text-center">
                    <div className="text-center">
                      <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                        {t('admin.no_services')}
                      </h3>
                      <p className="text-zinc-500 dark:text-zinc-400">
                        {canEdit ? t('admin.create_first_service') : t('admin.no_services_match_filters')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                services.map((service) => (
                  <motion.tr 
                    key={service.id} 
                    id={`service-row-${service.id}`}
                    className={`text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors ${
                      focusServiceId === service.id ? 'ring-2 ring-[#7d6633] ring-offset-2 ring-offset-white dark:ring-offset-zinc-900' : ''
                    }`}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    {/* Mobile-only selection */}
                    <td className="py-4 px-4 md:hidden">
                      <input
                        type="checkbox"
                        checked={selectedServices.has(service.id)}
                        onChange={() => handleSelectService(service.id)}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        title={`Select ${service.name}`}
                      />
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell tablet-hide-col">
                      <input
                        type="checkbox"
                        checked={selectedServices.has(service.id)}
                        onChange={() => handleSelectService(service.id)}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        title={`Select ${service.name}`}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-zinc-900 dark:text-zinc-100">
                          {service.name}
                        </div>
                        {service.description && (
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            {service.description}
                          </div>
                        )}
                        {/* Tablet: Show duration and price stacked (keeps tablet UX unchanged) */}
                        <div className="hidden md:block lg:hidden mt-1 space-y-0.5 tablet-stacked-info">
                          <div className="text-xs text-zinc-600 dark:text-zinc-300">
                            {formatDuration(service.duration_min)} • {formatPrice(service.price_cents)}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Mobile-only columns (use horizontal swipe-scroll) */}
                    <td className="py-4 px-4 md:hidden">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {formatDuration(service.duration_min)}
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell tablet-hide-col">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {formatDuration(service.duration_min)}
                      </div>
                    </td>
                    {/* Mobile-only columns (use horizontal swipe-scroll) */}
                    <td className="py-4 px-4 md:hidden">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {formatPrice(service.price_cents)}
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell tablet-hide-col">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {formatPrice(service.price_cents)}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        service.is_active
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {service.is_active ? t('admin.service_status_active') : t('admin.service_status_disabled')}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleOpenEditModal(service)}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={t('admin.edit_service_btn')}
                          >
                            <Icons.Edit />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleToggleStatus(service)}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={service.is_active ? t('admin.disable_service') : t('admin.enable_service')}
                          >
                            {service.is_active ? <Icons.Inactive /> : <Icons.Active />}
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteService(service)}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title={t('admin.delete_service_btn')}
                          >
                            <Icons.Delete />
                          </motion.button>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card className="mt-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between admin-pagination-wrapper">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 admin-pagination-info">
                {t('admin.showing_results_range', { from: ((pagination.page - 1) * pagination.pageSize) + 1, to: Math.min(pagination.page * pagination.pageSize, pagination.total), total: pagination.total, entity: t('admin.services_page_title').toLowerCase() })}
              </div>
              <div className="flex items-center gap-4 admin-pagination-controls">
                <div className="flex items-center gap-2 admin-pagination-buttons">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.previous')}
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {t('admin.page_of', { page: pagination.page, total: pagination.totalPages })}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.next')}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Create Service Modal */}
      {showCreateModal && (
        <PortalModal open={showCreateModal}>
          <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
            <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-lg w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
          >
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
                <Icons.Plus />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                {t('admin.create_service')}
              </h3>
            </div>
              
            <form onSubmit={handleCreateService}>
              {/* General Error */}
              {createErrors.general && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
                  {createErrors.general}
                </div>
              )}

              {/* Service Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.service_name_label')} *
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => handleCreateFormChange('name', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${createErrors.name ? 'border-red-500' : ''}`}
                  style={{ '--accent': ACCENT }}
                  placeholder={t('admin.service_name_placeholder')}
                />
                {createErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{createErrors.name}</p>
                )}
              </div>

              {/* Duration */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.duration_minutes_label')} *
                </label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={createForm.duration_min}
                  onChange={(e) => handleCreateFormChange('duration_min', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${createErrors.duration_min ? 'border-red-500' : ''}`}
                  style={{ '--accent': ACCENT }}
                  placeholder="e.g. 30"
                />
                <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                  {t('admin.duration_hint')}
                </p>
                {createErrors.duration_min && (
                  <p className="text-red-500 text-sm mt-1">{createErrors.duration_min}</p>
                )}
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.description_label')}
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => handleCreateFormChange('description', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                  placeholder={t('admin.service_description_placeholder')}
                />
              </div>

              {/* Price */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.price_cents_label')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={createForm.price_cents}
                  onChange={(e) => handleCreateFormChange('price_cents', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${createErrors.price_cents ? 'border-red-500' : ''}`}
                  style={{ '--accent': ACCENT }}
                  placeholder={t('admin.service_price_placeholder')}
                />
                <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                  {t('admin.price_hint')}
                </p>
                {createErrors.price_cents && (
                  <p className="text-red-500 text-sm mt-1">{createErrors.price_cents}</p>
                )}
              </div>

              {/* Assign Barbers */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  {t('admin.assign_barbers')} *
                </label>
                {loadingBarbers ? (
                  <div className="text-sm text-zinc-500 dark:text-zinc-400">{t('admin.loading_barbers')}</div>
                ) : barbers.length === 0 ? (
                  <div className="text-sm text-red-500">{t('admin.no_barbers_add_first')}</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto p-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
                    {barbers.map(barber => (
                      <label key={barber.id} className="flex items-center space-x-2 cursor-pointer hover:bg-white/30 dark:hover:bg-zinc-700/30 p-2 rounded-lg transition-all">
                        <input
                          type="checkbox"
                          checked={createForm.barber_user_ids.includes(barber.id)}
                          onChange={(e) => {
                            const newBarberIds = e.target.checked
                              ? [...createForm.barber_user_ids, barber.id]
                              : createForm.barber_user_ids.filter(id => id !== barber.id);
                            handleCreateFormChange('barber_user_ids', newBarberIds);
                          }}
                          className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-zinc-700 dark:text-zinc-300">{barber.name}</span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">({barber.email})</span>
                      </label>
                    ))}
                  </div>
                )}
                {createErrors.barber_user_ids && (
                  <p className="text-red-500 text-sm mt-1">{createErrors.barber_user_ids}</p>
                )}
                <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                  {t('admin.assign_barbers_hint')}
                </p>
              </div>

              {/* Active Toggle */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createForm.active}
                    onChange={(e) => handleCreateFormChange('active', e.target.checked)}
                    className="mr-2 rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-zinc-700 dark:text-zinc-300">
                    {t('admin.active_for_booking')}
                  </span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleCloseCreateModal}
                  disabled={creating}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('admin.cancel')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={creating || !createForm.name.trim() || !createForm.duration_min || createForm.barber_user_ids.length === 0}
                  className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: ACCENT }}
                >
                  {creating ? <Icons.Toggle /> : <Icons.Plus />}
                  <span>{creating ? t('admin.creating') : t('admin.create_service')}</span>
                </motion.button>
              </div>
            </form>
            </motion.div>
          </div>
        </PortalModal>
      )}

        {/* Edit Service Modal */}
        {showEditModal && selectedService && (
          <PortalModal open={showEditModal && !!selectedService}>
            <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-lg w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
              >
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
                    <Icons.Edit />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                      {t('admin.edit_service')}
                    </h3>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {selectedService.name}
                    </p>
                  </div>
                </div>
                
                <form onSubmit={handleEditService}>
                  {/* General Error */}
                  {editErrors.general && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
                      {editErrors.general}
                    </div>
                  )}

                  {/* Service Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      {t('admin.service_name_label')} *
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => handleEditFormChange('name', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${editErrors.name ? 'border-red-500' : ''}`}
                      style={{ '--accent': ACCENT }}
                      placeholder={t('admin.service_name_placeholder')}
                    />
                    {editErrors.name && (
                      <p className="text-red-500 text-sm mt-1">{editErrors.name}</p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      {t('admin.duration_minutes_label')} *
                    </label>
                    <input
                      type="number"
                      min="5"
                      step="5"
                      value={editForm.duration_min}
                      onChange={(e) => handleEditFormChange('duration_min', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${editErrors.duration_min ? 'border-red-500' : ''}`}
                      style={{ '--accent': ACCENT }}
                      placeholder="e.g. 30"
                    />
                    <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                      {t('admin.duration_hint')}
                    </p>
                    {editErrors.duration_min && (
                      <p className="text-red-500 text-sm mt-1">{editErrors.duration_min}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      {t('admin.description_label')}
                    </label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => handleEditFormChange('description', e.target.value)}
                      rows="3"
                      className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                      style={{ '--accent': ACCENT }}
                      placeholder={t('admin.service_description_placeholder')}
                    />
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      {t('admin.price_cents_label')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.price_cents}
                      onChange={(e) => handleEditFormChange('price_cents', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${editErrors.price_cents ? 'border-red-500' : ''}`}
                      style={{ '--accent': ACCENT }}
                      placeholder={t('admin.service_price_placeholder')}
                    />
                    <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                      {t('admin.price_edit_hint')}
                    </p>
                    {editErrors.price_cents && (
                      <p className="text-red-500 text-sm mt-1">{editErrors.price_cents}</p>
                    )}
                  </div>

                  {/* Assign Barbers */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      {t('admin.assign_barbers')} *
                    </label>
                    {loadingBarbers ? (
                      <div className="text-sm text-zinc-500 dark:text-zinc-400">{t('admin.loading_barbers')}</div>
                    ) : barbers.length === 0 ? (
                      <div className="text-sm text-red-500">{t('admin.no_barbers_add_first')}</div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto p-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
                        {barbers.map(barber => (
                          <label key={barber.id} className="flex items-center space-x-2 cursor-pointer hover:bg-white/30 dark:hover:bg-zinc-700/30 p-2 rounded-lg transition-all">
                            <input
                              type="checkbox"
                              checked={editForm.barber_user_ids.includes(barber.id)}
                              onChange={(e) => {
                                const newBarberIds = e.target.checked
                                  ? [...editForm.barber_user_ids, barber.id]
                                  : editForm.barber_user_ids.filter(id => id !== barber.id);
                                handleEditFormChange('barber_user_ids', newBarberIds);
                              }}
                              className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-zinc-700 dark:text-zinc-300">{barber.name}</span>
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">({barber.email})</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {editErrors.barber_user_ids && (
                      <p className="text-red-500 text-sm mt-1">{editErrors.barber_user_ids}</p>
                    )}
                    <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                      {t('admin.assign_barbers_hint')}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={handleCloseEditModal}
                      disabled={editing}
                      className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('admin.cancel')}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      disabled={editing || !editForm.name.trim() || !editForm.duration_min || editForm.barber_user_ids.length === 0}
                      className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: ACCENT }}
                    >
                      {editing ? <Icons.Toggle /> : <Icons.Edit />}
                      <span>{editing ? t('admin.updating') : t('admin.update_service')}</span>
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          </PortalModal>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && confirmData && (
          <PortalModal open={showConfirmModal}>
            <div className="hc-modal-overlay bg-black bg-opacity-50 flex items-start sm:items-center justify-center">
              <div className={`hc-modal-panel p-4 md:p-6 rounded-lg shadow-xl max-w-md w-full my-8 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium">
                    {confirmData.title}
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm mb-3">
                  {confirmData.message}
                </p>
                {confirmData.details && (
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm">
                    {confirmData.details.map((detail, index) => (
                      <div key={index}>{detail}</div>
                    ))}
                  </div>
                )}
                {confirmData.warning && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                    {confirmData.warning}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className={`px-4 py-2 rounded ${
                    isDark 
                      ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  {t('admin.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    confirmData.onConfirm();
                  }}
                  className={`px-4 py-2 rounded transition-all duration-200 text-white shadow-lg hover:shadow-xl ${
                    confirmData.type === 'delete' 
                      ? 'hover:bg-red-700'
                      : ''
                  }`}
                  style={confirmData.type === 'delete' ? {
                    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
                  } : {
                    background: 'linear-gradient(135deg, #c9a038 0%, #7d6633 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    if (confirmData.type === 'delete') {
                      e.target.style.boxShadow = '0 10px 25px rgba(220, 38, 38, 0.4)';
                    } else {
                      e.target.style.boxShadow = '0 10px 25px rgba(201, 160, 56, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    if (confirmData.type === 'delete') {
                      e.target.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
                    } else {
                      e.target.style.boxShadow = '0 4px 15px rgba(201, 160, 56, 0.3)';
                    }
                  }}
                >
                  {t('admin.confirm')}
                </button>
              </div>
              </div>
            </div>
          </PortalModal>
        )}

        {/* Delete Error Modal */}
        {showDeleteErrorModal && deleteErrorData && (
          <PortalModal open={showDeleteErrorModal}>
            <div className="hc-modal-overlay bg-black bg-opacity-50 flex items-start sm:items-center justify-center">
              <div className={`hc-modal-panel p-4 md:p-6 rounded-lg shadow-xl max-w-md w-full my-8 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium">
                    {t('admin.cannot_delete_service')}
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm">
                  {t('admin.service_has_bookings_error', { name: deleteErrorData.serviceName, count: deleteErrorData.bookingsCount })}
                </p>
              </div>
              
              <div className="mb-6">
                <p className="text-sm font-medium mb-2">{t('admin.resolve_this')}</p>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• {t('admin.resolve_disable_instead')}</li>
                  <li>• {t('admin.resolve_delete_bookings_first')}</li>
                </ul>
                <p className="text-xs mt-2 text-gray-500">
                  {t('admin.service_delete_protection_note')}
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteErrorModal(false)}
                  className={`px-4 py-2 rounded ${
                    isDark 
                      ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  {t('admin.understood')}
                </button>
              </div>
              </div>
            </div>
          </PortalModal>
        )}
    </div>
  );
};

export default Services;
